/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import store from "reducers/Store";
import {
  setCurrentUser,
  isCurrentUserTokenValid,
} from "actions/CurrentUserActions";
import getValidAccessToken from "utilities/getValidAccessToken";
import { DEFAULT_COOKIE_GROUPS } from "privacy/CookieGroup";
import {
  redactNumericPathSegments,
  redactUuids,
} from "utilities/StringUtilities";
import { signInAndIncrement } from "utilities/SignInLoopUtilities";

const updateOptions = (
  options: RequestInit,
  accessToken: AccessTokenInfo | null
): RequestInit => {
  const update = { ...options };
  if (accessToken) {
    const cookieGroups =
      window.adobePrivacy?.activeCookieGroups() ?? DEFAULT_COOKIE_GROUPS;
    update.headers = {
      ...update.headers,
      "X-Api-Key": process.env.ADOBE_IO_API_KEY!,
      "X-Cookie-Settings": cookieGroups.join(","),
      Authorization: `Bearer ${accessToken.token}`,
    };
  }
  return update;
};

function fakeJSON(response: Response): Response {
  return {
    ...response,
    json: (): Promise<any> => Promise.resolve({}),
  };
}

declare global {
  interface Window {
    requestsInFlight: number;
  }
}

function reauthenticateUser(): void {
  if (adobeIMS?.isSignedInUser()) {
    store.dispatch(setCurrentUser(null));
    console.log("sign in reauthenticate");
    signInAndIncrement();
  }
}

interface GatewayFetchAdditionalOptions {
  reportErrors?: boolean;
  reauthenticateOn401?: boolean;
  params?: Record<string, string>;
}

const defaultGatewayFetchOptions: GatewayFetchAdditionalOptions = {
  reportErrors: true,
  reauthenticateOn401: true,
};

export default async function gatewayFetch(
  path: string,
  options: RequestInit,
  additionalOptions?: GatewayFetchAdditionalOptions
): Promise<Response> {
  const { reportErrors, reauthenticateOn401 } = {
    ...defaultGatewayFetchOptions,
    ...additionalOptions,
  };
  const accessToken = await getValidAccessToken(options.keepalive !== true);
  const urlWithoutParams = `${process.env.PHONOS_GATEWAY_URL}${path}`;
  const params = additionalOptions?.params ?? {};
  params.time = new Date().getTime().toString();
  let url = `${urlWithoutParams}?${new URLSearchParams(params)}`;
  url = mapUrlForAnonymousUsers(url, accessToken);
  const redactedPath = redactNumericPathSegments(redactUuids(path));
  const redactedLocation = redactNumericPathSegments(
    redactUuids(window.location.pathname)
  );

  try {
    const fetchPromise = window.fetch(url, updateOptions(options, accessToken));
    window.requestsInFlight = (window.requestsInFlight ?? 0) + 1;

    const response = await fetchPromise;

    window.requestsInFlight = window.requestsInFlight - 1;

    if (!options.keepalive && response.status === 401 && reauthenticateOn401) {
      newrelic.noticeError(`GatewayFetch Response 401: Reauthenticated user`, {
        redactedPath,
        redactedLocation,
      });
      reauthenticateUser();
      return fakeJSON(response);
    }

    if (reportErrors && !response.ok) {
      newrelic.noticeError(
        `GatewayFetch ${redactedPath}: Status ${response.status}`,
        {
          redactedLocation,
        }
      );
    }

    return response;
  } catch (error) {
    window.requestsInFlight = window.requestsInFlight - 1;

    // Handle timeout errors
    if (error instanceof Error && error.name === "TimeoutError") {
      throw error;
    }

    // Ignore if page has been closed
    if (window.unloaded) {
      return fakeJSON({} as unknown as Response);
    }

    // Ignore if they offline
    if (!navigator.onLine) {
      return fakeJSON({} as unknown as Response);
    }

    // Ignore if IMS token is valid.
    // This most likely means the user is having a network issue.
    // It's not completely clear what is wrong with the user's
    // connection so let the caller decide what to do with the error.
    //
    // TODO: Everywhere we call gatewayFetch we should handle this.
    //       Or we could handle it here with a generic "Something went wrong."
    //       In some places we aren't do anything.
    if (await isCurrentUserTokenValid()) {
      newrelic.noticeError("GatewayFetch Network Error: Token Valid", {
        actualErrorName: error.name,
        actualErrorMessage: error.message,
        redactedPath,
        redactedLocation,
      });
      return fakeJSON({} as unknown as Response);
    }

    // The most likely scenario at this point is Adobe IO gateway is
    // blocking the OPTIONS request causing a CORS error when in reality
    // the user's token is invalid. We can reauthenticate the user here.
    newrelic.noticeError("GatewayFetch Network Error: Token Invalid", {
      actualErrorName: error.name,
      actualErrorMessage: error.message,
      redactedPath,
      redactedLocation,
    });

    reauthenticateUser();
    return fakeJSON({} as unknown as Response);
  }
}

const mapUrlForAnonymousUsers = (
  url: string,
  token: AccessTokenInfo | null
): string => {
  if (!token || !token.isGuestToken) {
    return url;
  }

  if (url.includes("api/v1/anonymous/")) {
    return url;
  }

  return url.replace("api/v1/", "api/v1/anonymous/");
};
